import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "reactstrap";
import { Button, Checkbox, DefaultSelect } from "ui-kit-takeat";
import {
  getBrands,
  createBrand,
  updateBrand,
  deleteBrand as deleteBrandApi,
  setDefaultBrand,
} from "services/brandsService";
import BrandCard from "./BrandCard";
import BrandFormModal from "./BrandFormModal";
import BrandDeleteModal from "./BrandDeleteModal";
import BrandSetDefaultModal from "./BrandSetDefaultModal";
import BrandImportModal from "./BrandImportModal";
import { Content, Header, BrandsGrid, CenterMessage, NfceArea } from "./styles";

const tributaryRegimOptions = [
  { value: "1", label: "Simples Nacional" },
  { value: "3", label: "Regime Normal" },
];

function BrandsSection({ restaurantId, token, notify, restaurantData, setRestaurantData, refreshKey = 0, onBrandsChanged }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [saving, setSaving] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [isSetDefaultModalOpen, setIsSetDefaultModalOpen] = useState(false);
  const [brandToSetDefault, setBrandToSetDefault] = useState(null);
  const [settingDefault, setSettingDefault] = useState(false);

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [brandToImport, setBrandToImport] = useState(null);

  const [tributaryMode, setTributaryMode] = useState(null);

  useEffect(() => {
    if (restaurantData?.regime_tributario === "1") {
      setTributaryMode({ value: "1", label: "Simples Nacional" });
    }
    if (restaurantData?.regime_tributario === "3") {
      setTributaryMode({ value: "3", label: "Regime Normal" });
    }
  }, [restaurantData?.regime_tributario]);

  const handleRestaurantChange = useCallback(
    (field, value) => {
      setRestaurantData((prev) => ({ ...prev, [field]: value }));
    },
    [setRestaurantData]
  );

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await getBrands(restaurantId, token);
      setBrands(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, token]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands, refreshKey]);

  const handleOpenCreate = useCallback(() => {
    setSelectedBrand(null);
    setIsFormModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((brand) => {
    setSelectedBrand(brand);
    setIsFormModalOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setIsFormModalOpen(false);
    setSelectedBrand(null);
  }, []);

  const handleSave = useCallback(
    async (formData) => {
      setSaving(true);
      try {
        if (selectedBrand) {
          await updateBrand(selectedBrand.id, formData, token);
          notify("tr", "success", "Marca atualizada com sucesso!");
        } else {
          await createBrand(restaurantId, formData, token);
          notify("tr", "success", "Marca criada com sucesso!");
        }
        handleCloseForm();
        fetchBrands();
        // O slug da brand default sincroniza restaurants.nickname no backend;
        // refetch do restaurante evita que o form da página guarde o valor
        // antigo e o reverta num "Atualizar" posterior (TAT1C0-169).
        if (onBrandsChanged) onBrandsChanged();
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Erro ao salvar marca.";
        notify("tr", "danger", msg);
      } finally {
        setSaving(false);
      }
    },
    [selectedBrand, restaurantId, token, notify, handleCloseForm, fetchBrands, onBrandsChanged]
  );

  const handleOpenDelete = useCallback((brand) => {
    setBrandToDelete(brand);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
    setBrandToDelete(null);
  }, []);

  const handleOpenSetDefault = useCallback((brand) => {
    setBrandToSetDefault(brand);
    setIsSetDefaultModalOpen(true);
  }, []);

  const handleCloseSetDefault = useCallback(() => {
    setIsSetDefaultModalOpen(false);
    setBrandToSetDefault(null);
  }, []);

  const handleConfirmSetDefault = useCallback(async () => {
    if (!brandToSetDefault) return;
    setSettingDefault(true);
    try {
      await setDefaultBrand(brandToSetDefault.id, token);
      notify("tr", "success", "Marca padrão atualizada com sucesso!");
      handleCloseSetDefault();
      fetchBrands();
      if (onBrandsChanged) onBrandsChanged();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Erro ao tornar a marca padrão.";
      notify("tr", "danger", msg);
    } finally {
      setSettingDefault(false);
    }
  }, [brandToSetDefault, token, notify, handleCloseSetDefault, fetchBrands, onBrandsChanged]);

  const handleOpenImport = useCallback((brand) => {
    setBrandToImport(brand);
    setIsImportModalOpen(true);
  }, []);

  const handleCloseImport = useCallback(() => {
    setIsImportModalOpen(false);
    setBrandToImport(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!brandToDelete) return;
    setDeleting(true);
    try {
      await deleteBrandApi(brandToDelete.id, token);
      notify("tr", "success", "Marca excluída com sucesso!");
      handleCloseDelete();
      fetchBrands();
      if (onBrandsChanged) onBrandsChanged();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Erro ao excluir marca.";
      notify("tr", "danger", msg);
    } finally {
      setDeleting(false);
    }
  }, [brandToDelete, token, notify, handleCloseDelete, fetchBrands, onBrandsChanged]);

  const hasNfce = !!restaurantData?.has_nfce;

  return (
    <Content>
      <Header>
        <span>Marcas</span>
        <Button
          title="+ Criar Marca"
          buttonColor="#007bff"
          onClick={handleOpenCreate}
          containerStyles={{ height: 36 }}
        />
      </Header>

      {loading && (
        <CenterMessage>
          <Spinner color="secondary" />
        </CenterMessage>
      )}

      {!loading && error && (
        <CenterMessage>
          <span>Erro ao carregar marcas.</span>
          <Button
            title="Tentar novamente"
            buttonColor="#6c757d"
            onClick={fetchBrands}
            containerStyles={{ height: 32 }}
          />
        </CenterMessage>
      )}

      {!loading && !error && brands.length > 0 && (
        <BrandsGrid>
          {brands.map((brand) => (
            <BrandCard
              key={brand.id}
              brand={brand}
              onEdit={handleOpenEdit}
              onDelete={handleOpenDelete}
              onImport={handleOpenImport}
              onSetDefault={handleOpenSetDefault}
            />
          ))}
        </BrandsGrid>
      )}

      <NfceArea>
        <Checkbox
          label="Emissão NFCe"
          checked={hasNfce}
          onClick={(e) => handleRestaurantChange("has_nfce", e)}
        />
        <DefaultSelect
          label="Regime tributário"
          value={tributaryMode}
          onChange={(e) => {
            handleRestaurantChange("regime_tributario", e.value);
            setTributaryMode(e);
          }}
          options={tributaryRegimOptions}
          containerStyles={{ width: 250 }}
        />
      </NfceArea>

      <BrandFormModal
        isOpen={isFormModalOpen}
        toggle={handleCloseForm}
        brand={selectedBrand}
        onSave={handleSave}
        saving={saving}
        hasNfce={hasNfce}
      />

      <BrandDeleteModal
        isOpen={isDeleteModalOpen}
        toggle={handleCloseDelete}
        brand={brandToDelete}
        onConfirm={handleConfirmDelete}
        deleting={deleting}
      />

      <BrandSetDefaultModal
        isOpen={isSetDefaultModalOpen}
        toggle={handleCloseSetDefault}
        brand={brandToSetDefault}
        onConfirm={handleConfirmSetDefault}
        saving={settingDefault}
      />

      <BrandImportModal
        isOpen={isImportModalOpen}
        toggle={handleCloseImport}
        brand={brandToImport}
        restaurantId={restaurantId}
        restaurantName={restaurantData?.fantasy_name}
        token={token}
        notify={notify}
      />
    </Content>
  );
}

export default BrandsSection;
